import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../shared/api/supabase-client';
import { useAuthStore } from '../../shared/store/authStore';
import { ROUTES } from '../../shared/config/routes';
import { toast } from 'sonner@2.0.3';
import { resetMontageStore } from '../../features/montage/store';

const PUBLIC_PATHS = [ROUTES.LOGIN, '/oauth/callback', ROUTES.PARTNER_JOIN];

/** Партнёр — по пометке в учётке Supabase, её ставит сервер при входе по приглашению. */
const isPartner = (user: { app_metadata?: Record<string, unknown> } | null | undefined) =>
  user?.app_metadata?.role === 'partner';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, clearUser, isLoading, userId } = useAuthStore();
  // getSession и SIGNED_IN могут грузить разные профили одновременно при
  // быстром выходе/входе. Только последний запуск вправе обновить auth-store.
  const authGenerationRef = useRef(0);

  useEffect(() => {
    let active = true;

    async function initSession() {
      const generation = ++authGenerationRef.current;
      try {
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<any>((_, reject) => 
          setTimeout(() => reject(new Error('getSession timeout')), 5000)
        );
        
        const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);
        if (!active || generation !== authGenerationRef.current) return;

        if (session) {
          await loadUserProfile(session.user.id, session.access_token, generation, isPartner(session.user));
        } else {
          clearUser();
        }
      } catch (err) {
        console.error('initSession failed or timed out:', err);
        if (active && generation === authGenerationRef.current) clearUser();
      }
    }

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'INITIAL_SESSION') return; // Игнорируем, так как сессия инициализирована в initSession()

      if (event === 'SIGNED_IN' && session) {
        const generation = ++authGenerationRef.current;
        const s = useAuthStore.getState();
        if (s.userId === session.user.id && s.role !== null) {
          s.setUser({ userId: s.userId, role: s.role, assignedAccountIds: s.assignedAccountIds, accessToken: session.access_token });
        } else {
          resetMontageStore();
          // Новый пользователь уже вошёл, но его профиль ещё едет. Не держим
          // в auth-store предыдущий userId: иначе защищённый Layout успевает
          // показать его имя, меню и данные под новой Supabase-сессией.
          clearUser();
          await loadUserProfile(session.user.id, session.access_token, generation, isPartner(session.user));
        }
      }
      if (event === 'SIGNED_OUT') {
        authGenerationRef.current += 1;
        resetMontageStore();
        clearUser();
      }
      if (event === 'TOKEN_REFRESHED' && session) {
        useAuthStore.getState().setUser({
          userId: session.user.id,
          role: useAuthStore.getState().role ?? 'employee',
          assignedAccountIds: useAuthStore.getState().assignedAccountIds,
          accessToken: session.access_token,
        });
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [clearUser]); // Подписка создается один раз при монтировании

  // Обработка редиректов
  useEffect(() => {
    if (!isLoading && !userId && !PUBLIC_PATHS.includes(location.pathname)) {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [isLoading, userId, location.pathname, navigate]);

  async function loadUserProfile(userId: string, accessToken: string, generation = authGenerationRef.current, partner = false) {
    // У партнёра нет ни профиля сотрудника, ни назначений — нечего и читать.
    if (partner) {
      if (generation === authGenerationRef.current) setUser({ userId, role: 'partner', assignedAccountIds: [], accessToken });
      return;
    }
    // Ретраи: временный сбой профиля раньше молча ронял в role='employee' с пустыми
    // назначениями → все аккаунты скрывались («Нет активных аккаунтов»). Повторяем,
    // и только после исчерпания показываем ошибку (не путать с «нет аккаунтов»).
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const profileQuery = supabase
          .from('user_profiles')
          .select('role')
          .eq('id', userId)
          .maybeSingle(); // нет строки → data:null без throw

        const assignmentsQuery = supabase
          .from('user_account_assignments')
          .select('account_id')
          .eq('user_id', userId);

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('loadUserProfile timeout')), 10_000)
        );

        const [profileResult, assignmentsResult] = await Promise.race([
          Promise.all([profileQuery, assignmentsQuery]),
          timeoutPromise,
        ]);

        if (profileResult.error) throw profileResult.error;
        if (assignmentsResult.error) throw assignmentsResult.error;

        // maybeSingle() отдаёт null и когда строки нет, и когда её прячет RLS —
        // роль тихо становится 'employee', хотя сервер (service-role) видит admin.
        if (!profileResult.data) {
          console.warn(`user_profiles: строка для ${userId} не прочитана (нет записи или RLS) — роль по умолчанию 'employee'`);
        }

        if (generation !== authGenerationRef.current) return;
        setUser({
          userId,
          role: profileResult.data?.role ?? 'employee',
          assignedAccountIds: assignmentsResult.data?.map((a: any) => a.account_id) ?? [],
          accessToken,
        });
        return;
      } catch (err) {
        console.error(`loadUserProfile attempt ${attempt}/3 failed:`, err);
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, 1500 * attempt));
          continue;
        }
        toast.error('Не удалось загрузить профиль. Обновите страницу.');
        if (generation === authGenerationRef.current) {
          setUser({ userId, role: 'employee', assignedAccountIds: [], accessToken });
        }
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return <>{children}</>;
}
